import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  Alert,
  TextInput,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { useAppTheme } from '../../contexts/ThemeContext';
import { TRADE_TYPES } from '../../lib/tradeTypes';

export default function SignUpScreen({ navigation }) {
  const { colors, spacing, typography, radius } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, typography, radius), [colors]);

  const { signUp } = useAuth();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    businessName: '',
    password: '',
    confirmPassword: '',
    tradeType: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [customTrade, setCustomTrade] = useState('');

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  }

  function validate() {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.businessName.trim()) e.businessName = 'Business name is required';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 8) e.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!form.tradeType) e.tradeType = 'Select your trade type';
    else if (form.tradeType === 'Other' && !customTrade.trim()) e.tradeType = 'Enter your trade type';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSignUp() {
    if (!validate()) return;
    setLoading(true);
    try {
      const result = await signUp({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        fullName: form.fullName.trim(),
        businessName: form.businessName.trim(),
        tradeType: form.tradeType === 'Other' ? customTrade.trim() : form.tradeType,
      });
      // If Supabase email confirmation is enabled, there's no session yet.
      // Navigate to Onboarding only if a session exists (confirmation disabled).
      // Otherwise, prompt the user to check their email.
      if (result?.session) {
        navigation.navigate('Onboarding');
      } else {
        Alert.alert(
          'Check Your Email',
          `We sent a confirmation link to ${form.email.trim()}. Please verify your email before signing in.`,
          [{ text: 'OK', onPress: () => navigation.navigate('Login') }]
        );
      }
    } catch (err) {
      Alert.alert('Sign Up Failed', err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <SafeAreaView style={styles.safe}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.flex}
      >
        <ScrollView
          contentContainerStyle={styles.scroll}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
          showsVerticalScrollIndicator={false}
        >
          <TouchableOpacity
            style={styles.backBtn}
            onPress={() => navigation.goBack()}
            activeOpacity={0.7}
          >
            <Text style={styles.backText}>← Back</Text>
          </TouchableOpacity>

          <Text style={styles.heading}>Create account</Text>
          <Text style={styles.subheading}>Join thousands of contractors using Slate</Text>

          <Input
            label="Full Name"
            value={form.fullName}
            onChangeText={(t) => update('fullName', t)}
            placeholder="John Smith"
            error={errors.fullName}
            autoCapitalize="words"
          />

          <Input
            label="Business Name"
            value={form.businessName}
            onChangeText={(t) => update('businessName', t)}
            placeholder="Smith Contracting LLC"
            error={errors.businessName}
            autoCapitalize="words"
          />

          <Input
            label="Email"
            value={form.email}
            onChangeText={(t) => update('email', t)}
            placeholder="you@company.com"
            keyboardType="email-address"
            autoCapitalize="none"
            error={errors.email}
          />

          <View style={styles.fieldWrapper}>
            <Text style={styles.fieldLabel}>Trade Type</Text>
            <TouchableOpacity
              style={[
                styles.picker,
                errors.tradeType && styles.pickerError,
              ]}
              onPress={() => setShowTradeModal(true)}
              activeOpacity={0.75}
            >
              <Text
                style={[
                  styles.pickerText,
                  !form.tradeType && !customTrade && styles.pickerPlaceholder,
                ]}
              >
                {form.tradeType === 'Other' ? (customTrade || 'Select your trade...') : (form.tradeType || 'Select your trade...')}
              </Text>
              <Text style={styles.pickerChevron}>›</Text>
            </TouchableOpacity>
            {errors.tradeType ? (
              <Text style={styles.errorText}>{errors.tradeType}</Text>
            ) : null}
          </View>

          {showTradeModal && (
            <View style={styles.tradeList}>
              {TRADE_TYPES.map((trade) => (
                <TouchableOpacity
                  key={trade}
                  style={[
                    styles.tradeOption,
                    form.tradeType === trade && styles.tradeOptionSelected,
                  ]}
                  onPress={() => {
                    update('tradeType', trade);
                    if (trade !== 'Other') setShowTradeModal(false);
                    else setShowTradeModal(false);
                  }}
                  activeOpacity={0.7}
                >
                  <Text
                    style={[
                      styles.tradeOptionText,
                      form.tradeType === trade && styles.tradeOptionTextSelected,
                    ]}
                  >
                    {trade}
                  </Text>
                  {form.tradeType === trade && (
                    <Text style={styles.checkmark}>✓</Text>
                  )}
                </TouchableOpacity>
              ))}
            </View>
          )}

          {form.tradeType === 'Other' && (
            <View style={styles.fieldWrapper}>
              <Text style={styles.fieldLabel}>Describe Your Trade</Text>
              <TextInput
                style={[styles.customInput, { color: colors.text, borderColor: errors.tradeType ? colors.error || '#ef4444' : colors.border, backgroundColor: colors.surface }]}
                value={customTrade}
                onChangeText={(v) => { setCustomTrade(v); if (errors.tradeType) setErrors(e => ({ ...e, tradeType: null })); }}
                placeholder="e.g. Tile Installer, Welder..."
                placeholderTextColor={colors.textMuted}
                autoFocus
                returnKeyType="done"
              />
            </View>
          )}

          <Input
            label="Password"
            value={form.password}
            onChangeText={(t) => update('password', t)}
            placeholder="Min. 8 characters"
            secureTextEntry
            error={errors.password}
          />

          <Input
            label="Confirm Password"
            value={form.confirmPassword}
            onChangeText={(t) => update('confirmPassword', t)}
            placeholder="Repeat your password"
            secureTextEntry
            error={errors.confirmPassword}
          />

          <Button
            title="Create Account"
            onPress={handleSignUp}
            loading={loading}
            size="lg"
            style={styles.submitBtn}
          />

          <View style={styles.footer}>
            <Text style={styles.footerText}>Already have an account? </Text>
            <TouchableOpacity
              onPress={() => navigation.navigate('Login')}
              activeOpacity={0.7}
            >
              <Text style={styles.footerLink}>Sign in</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.terms}>
            By creating an account, you agree to our Terms of Service and Privacy Policy.
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

function createStyles(colors, spacing, typography, radius) {
  return StyleSheet.create({
    safe: {
      flex: 1,
      backgroundColor: colors.background,
    },
    flex: {
      flex: 1,
    },
    scroll: {
      flexGrow: 1,
      paddingHorizontal: spacing.xl,
      paddingBottom: spacing.xxxl,
    },
    backBtn: {
      marginTop: spacing.base,
      marginBottom: spacing.xl,
      alignSelf: 'flex-start',
      paddingVertical: spacing.sm,
    },
    backText: {
      fontSize: typography.sizes.base,
      color: colors.accent,
    },
    heading: {
      fontSize: typography.sizes.xxl,
      fontWeight: typography.weights.bold,
      color: colors.text,
      letterSpacing: -0.8,
      marginBottom: spacing.xs,
    },
    subheading: {
      fontSize: typography.sizes.base,
      color: colors.textSecondary,
      marginBottom: spacing.xl + spacing.sm,
    },
    fieldWrapper: {
      marginBottom: spacing.base,
    },
    fieldLabel: {
      fontSize: typography.sizes.sm,
      fontWeight: typography.weights.medium,
      color: colors.textSecondary,
      marginBottom: spacing.xs + 2,
      letterSpacing: 0.3,
    },
    picker: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.surface,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.base,
      paddingVertical: spacing.md,
      minHeight: 48,
    },
    pickerError: {
      borderColor: colors.danger,
    },
    pickerText: {
      fontSize: typography.sizes.base,
      color: colors.text,
    },
    pickerPlaceholder: {
      color: colors.textMuted,
    },
    pickerChevron: {
      fontSize: 20,
      color: colors.textMuted,
      fontWeight: '300',
    },
    errorText: {
      fontSize: typography.sizes.xs,
      color: colors.danger,
      marginTop: spacing.xs,
      marginLeft: spacing.xs,
    },
    tradeList: {
      backgroundColor: colors.surfaceElevated,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      marginBottom: spacing.base,
      overflow: 'hidden',
    },
    tradeOption: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.base,
      paddingVertical: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    tradeOptionSelected: {
      backgroundColor: colors.accentLight,
    },
    tradeOptionText: {
      fontSize: typography.sizes.base,
      color: colors.textSecondary,
    },
    tradeOptionTextSelected: {
      color: colors.accent,
      fontWeight: typography.weights.medium,
    },
    checkmark: {
      color: colors.accent,
      fontSize: typography.sizes.base,
      fontWeight: typography.weights.bold,
    },
    customInput: {
      borderRadius: radius.md,
      borderWidth: 1,
      paddingHorizontal: spacing.base,
      height: 48,
      fontSize: typography.sizes.base,
    },
    submitBtn: {
      marginTop: spacing.sm,
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: spacing.xl,
    },
    footerText: {
      fontSize: typography.sizes.base,
      color: colors.textSecondary,
    },
    footerLink: {
      fontSize: typography.sizes.base,
      color: colors.accent,
      fontWeight: typography.weights.semibold,
    },
    terms: {
      fontSize: typography.sizes.xs,
      color: colors.textMuted,
      textAlign: 'center',
      marginTop: spacing.base,
      lineHeight: 18,
      paddingHorizontal: spacing.base,
    },
  });
}
